/**
 * Attractions is a CATEGORY page — one level below the Things to Do hub, at
 * /family-activities-in-santa-barbara/attractions. This is where the old
 * /santa-barbara-family-attractions URL redirects.
 *
 * Its distinct job: attractions are paid destinations you choose BETWEEN,
 * so the page leads with a comparison table rather than a list.
 */

export const attractionsIntro = {
  count: '22 attractions',
  updated: 'Prices checked August 2026',
  deck:
    'The zoo, the museums, the wharf and everything else worth paying for. Compared on the two things that actually decide it — what it costs a family of four, and how long it holds a kid’s attention.',
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/8331d995-ee60-4940-99b5-facea8aafa38.jpg',
  heroAlt:
    'Red-tiled rooftops and palm trees looking out over the ocean on a hazy Santa Barbara afternoon',
}

export interface ComparisonRow {
  name: string
  familyOf4: string
  duration: string
  bestAge: string
  setting: string
  membership: string
  membershipGood: boolean
}

export const comparison: ComparisonRow[] = [
  {
    name: 'Santa Barbara Zoo',
    familyOf4: '$96',
    duration: '2–3 hrs',
    bestAge: '1–8',
    setting: 'Outdoor',
    membership: 'Pays off in 3 visits',
    membershipGood: true,
  },
  {
    name: 'MOXI',
    familyOf4: '$76',
    duration: '3 hrs',
    bestAge: '4–11',
    setting: 'Indoor',
    membership: 'Pays off in 3 visits',
    membershipGood: true,
  },
  {
    name: 'Sea Center',
    familyOf4: '$36',
    duration: '45 min',
    bestAge: '2–9',
    setting: 'Indoor',
    membership: 'Included with NHM',
    membershipGood: true,
  },
  {
    name: 'Natural History Museum',
    familyOf4: '$60',
    duration: '2 hrs',
    bestAge: '4–12',
    setting: 'Both',
    membership: 'Pays off in 3 visits',
    membershipGood: true,
  },
  {
    name: 'Botanic Garden',
    familyOf4: '$54',
    duration: '1.5 hrs',
    bestAge: '3–10',
    setting: 'Outdoor',
    membership: 'Pays off in 4 visits',
    membershipGood: true,
  },
  {
    name: 'Stearns Wharf',
    familyOf4: 'Free',
    duration: '1 hr',
    bestAge: 'All',
    setting: 'Outdoor',
    membership: 'Just pay for parking',
    membershipGood: false,
  },
  {
    name: 'Old Mission',
    familyOf4: '$30',
    duration: '1 hr',
    bestAge: '7+',
    setting: 'Both',
    membership: 'Not worth it',
    membershipGood: false,
  },
  {
    name: 'Courthouse clock tower',
    familyOf4: 'Free',
    duration: '30 min',
    bestAge: '4+',
    setting: 'Both',
    membership: 'Free, no ticket',
    membershipGood: false,
  },
]

export interface FeaturedAttraction {
  slug: string
  name: string
  area: string
  image: string
  imageAlt: string
  hook: string
  insider: string
  ages: string
  cost: string
  visit: string
  setting: string
}

export const featured: FeaturedAttraction[] = [
  {
    slug: 'santa-barbara-zoo',
    name: 'Santa Barbara Zoo',
    area: 'Waterfront',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4dd3450c-c850-4ba5-bf3c-4a832c22d326.jpg',
    imageAlt:
      'A family walking a palm-lined zoo path with giraffes and the ocean in the distance',
    hook: 'Small enough to finish before a nap and the only zoo we know with an ocean view. The hillside lawn at the top is the best picnic spot in town.',
    insider:
      'Park on Niños Drive instead of the lot and walk in — it is free and closer than half the lot.',
    ages: '1–8',
    cost: '$24 adult / $16 child',
    visit: '2–3 hours',
    setting: 'Outdoor',
  },
  {
    slug: 'moxi-wolf-museum',
    name: 'MOXI, The Wolf Museum',
    area: 'Funk Zone',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/bbf5e86a-7c5c-4fc3-9b25-f26ddd03ff4e.jpg',
    imageAlt:
      'Children playing at a large interactive exhibit inside a bright science museum',
    hook: 'Three floors of hands-on science and a rooftop sky garden with a glass-floor bridge that every kid crosses exactly once and then again forty times.',
    insider:
      'Go at ten on a weekday. School groups land at eleven and it becomes a different building.',
    ages: '4–11',
    cost: '$20 adult / $18 child',
    visit: '3 hours',
    setting: 'Indoor',
  },
  {
    slug: 'sea-center-stearns-wharf',
    name: 'Sea Center',
    area: 'Stearns Wharf',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4aaa00a1-102b-40ca-96c4-944f6fcc7243.jpg',
    imageAlt:
      'A child’s hand reaching into a shallow aquarium touch tank with sea stars',
    hook: 'Tiny, touchable and a perfect forty-five minutes — which is genuinely its advantage over anywhere bigger. Two rooms, no wandering.',
    insider:
      'Touch tanks are only staffed on the hour. Show up at :55 or you are looking at water.',
    ages: '2–9',
    cost: '$10 adult / $8 child',
    visit: '45 minutes',
    setting: 'Indoor',
  },
  {
    slug: 'santa-barbara-botanic-garden',
    name: 'Santa Barbara Botanic Garden',
    area: 'Mission Canyon',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/c24f9e02-9e78-479a-b97b-1576916b8601.jpg',
    imageAlt:
      'A shaded woodland trail with a small wooden footbridge over a dry creek bed',
    hook: 'Seventy-eight acres of trails, a creek, and a redwood grove that feels three hundred miles north of here. The most underrated place on this list with kids who like to run.',
    insider:
      'The Pritchett Trail loop is stroller-passable. Everything past the dam is not.',
    ages: '3–10',
    cost: '$16 adult / $8 child',
    visit: '1.5 hours',
    setting: 'Outdoor',
  },
]

export interface AttractionRow {
  id: string
  name: string
  area: string
  cost: string
  ages: string
  duration: string
  blurb: string
  tags: string[]
}

export const moreAttractions: AttractionRow[] = [
  {
    id: 'a1',
    name: 'Santa Barbara Museum of Natural History',
    area: 'Mission Canyon',
    cost: '$$',
    ages: '4–12',
    duration: '2 hrs',
    blurb:
      'The blue whale skeleton out front is worth the trip alone. The creek trail behind the museum is free, shaded and rarely busy — go there after.',
    tags: ['Museums', 'Indoor', 'Mission Canyon'],
  },
  {
    id: 'a2',
    name: 'Stearns Wharf',
    area: 'Waterfront',
    cost: 'Free',
    ages: 'All',
    duration: '1 hr',
    blurb:
      'Free, always open, endlessly interesting to a four-year-old. Park at the far end, walk out for ice cream, watch the pelicans, go home.',
    tags: ['Free', 'Outdoor', 'Waterfront'],
  },
  {
    id: 'a3',
    name: 'Lil’ Toot water taxi',
    area: 'Harbor',
    cost: '$',
    ages: '2–10',
    duration: '15 min',
    blurb:
      'A tiny yellow boat between the wharf and the harbor. Twelve minutes, a few dollars, and the highlight of the day for anyone under six.',
    tags: ['Outdoor', 'Waterfront'],
  },
  {
    id: 'a4',
    name: 'Courthouse clock tower',
    area: 'Downtown',
    cost: 'Free',
    ages: '4+',
    duration: '30 min',
    blurb:
      'An elevator, a spiral stair and the best free view in the county. Twenty minutes total, which makes it the perfect thing to bolt onto a downtown errand.',
    tags: ['Free', 'Downtown', 'Indoor'],
  },
  {
    id: 'a5',
    name: 'Old Mission Santa Barbara',
    area: 'Mission Canyon',
    cost: '$',
    ages: '7+',
    duration: '1 hr',
    blurb:
      'Genuinely interesting once kids are old enough for the fourth-grade mission unit, and pretty dull before that. The rose garden across the street is the real draw for little ones.',
    tags: ['Museums', 'Mission Canyon', 'Big kids'],
  },
  {
    id: 'a6',
    name: 'Chase Palm Park carousel',
    area: 'Waterfront',
    cost: '$',
    ages: '0–6',
    duration: '30 min',
    blurb:
      'A few dollars a ride, right next to the shipwreck playground. Together they make a full morning for under ten dollars.',
    tags: ['Outdoor', 'Waterfront', 'Toddlers'],
  },
]

export const attractionsTier3 = [
  {
    area: 'Museums & history',
    items: [
      'SB Museum of Art',
      'Maritime Museum',
      'Karpeles Manuscript Library',
      'Casa del Herrero',
    ],
  },
  {
    area: 'Animals & nature',
    items: [
      'Andree Clark Bird Refuge',
      'Elings Park nature trails',
      'Arroyo Hondo Preserve',
      'Coronado Butterfly Preserve',
    ],
  },
  {
    area: 'Rides & play',
    items: [
      'Rockin’ Jump trampoline park',
      'AMF Bowling',
      'SB Adventure Company kayak tours',
      'Land Shark harbor tour',
    ],
  },
  {
    area: 'Just outside town',
    items: [
      'Ostrich Land, Buellton',
      'Solvang windmills',
      'Channel Islands boat trip',
      'Nojoqui Falls',
    ],
  },
]

export const attractionsFilters = [
  { group: 'Type', options: ['Museums', 'Outdoor', 'Indoor', 'Free'] },
  { group: 'Where', options: ['Downtown', 'Waterfront', 'Mission Canyon'] },
  { group: 'Ages', options: ['Toddlers', 'Big kids'] },
]

export const attractionsFaqs = [
  {
    question: 'What are the best attractions in Santa Barbara for kids?',
    answer:
      'The zoo and MOXI are the two that work for the widest age range and are worth a membership if you will go more than three times. For a shorter outing, the Sea Center is forty-five minutes and the Courthouse clock tower is free.',
  },
  {
    question: 'Which Santa Barbara attractions are free with kids?',
    answer:
      'Stearns Wharf, the Courthouse clock tower, the Andree Clark Bird Refuge and the Natural History Museum’s creek trail are all free. The Botanic Garden and the art museum both run free admission days each month.',
  },
  {
    question: 'Is a Santa Barbara Zoo membership worth it?',
    answer:
      'If you live here and have a child under eight, yes. A family membership costs roughly what three visits cost, and the zoo is small enough that a forty-minute drop-in actually works — which is what makes the membership pay off.',
  },
  {
    question: 'What is there to do with kids when it rains?',
    answer:
      'MOXI, the Sea Center and the Natural History Museum are the three indoor attractions that hold up on a rainy day. The full rainy-day guide has eighteen more.',
  },
]
